import { type TranscriptCue, normalizeSrtTimecode } from '@bynder-studio/render-core';

/**
 * Format seconds into a short timecode string (MM:SS), optionally with milliseconds.
 * Used only for the cue editor UI; cues themselves store canonical SRT timecodes.
 */
export function formatShortTimecode(seconds: number, withMilliseconds = false): string {
    const totalMs = Math.max(0, Math.round(seconds * 1000));
    const totalSecs = Math.floor(totalMs / 1000);
    const secs = totalSecs % 60;
    const mins = Math.floor(totalSecs / 60);
    const ms = totalMs % 1000;

    const base = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    return withMilliseconds ? `${base},${String(ms).padStart(3, '0')}` : base;
}

/**
 * Parse a short timecode string (MM:SS or MM:SS,mmm) into total seconds.
 */
export function parseShortTimecode(timecode: string): number {
    const [minutesPart, secondsPart = '0'] = timecode.trim().split(':');
    const [seconds, milliseconds = '0'] = secondsPart.split(',');
    const minutes = Number(minutesPart);

    return minutes * 60 + Number(seconds) + Number(milliseconds) / 1000;
}

/**
 * Convert an array of TranscriptCues back into an SRT string.
 * Cues store canonical SRT timecodes, so no conversion is required.
 */
export function cuesToSrt(cues: TranscriptCue[]): string {
    return cues.map((cue, i) => `${i + 1}\n${cue.startTime} --> ${cue.endTime}\n${cue.text}`).join('\n\n');
}

/**
 * Parse an SRT file content string into TranscriptCue[].
 *
 * SRT format:
 *   1
 *   00:00:01,000 --> 00:00:04,000
 *   Hello, welcome to our video!
 *
 *   2
 *   00:00:04,000 --> 00:00:08,000
 *   This is a transcript demo.
 */
export function parseSrt(srtContent: string): TranscriptCue[] {
    const cues: TranscriptCue[] = [];
    // Split on double newline (handles \r\n and \n)
    const blocks = srtContent.trim().replace(/\r\n/g, '\n').split(/\n\n+/);

    blocks.forEach((block) => {
        const lines = block.split('\n');

        // Keep cues even when text is intentionally empty (index + timecode lines only)
        if (lines.length < 2) {
            return;
        }

        const index = lines[0].trim();
        // Intentionally unanchored/lenient here: parseSrt tolerates already-accepted
        // content. Strict validation uses the anchored SRT_TIMECODE_REGEX below.
        const timecodeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);

        if (!timecodeMatch) {
            return;
        }

        const text = lines.slice(2).join('\n').trim();

        cues.push({
            id: `srt-cue-${index}`,
            startTime: normalizeSrtTimecode(timecodeMatch[1]),
            endTime: normalizeSrtTimecode(timecodeMatch[2]),
            text,
        });
    });

    return cues;
}
